import { cloneElement, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import IconButton from "../IconButton";
import { withTranslation } from "@template/components/translation";
import { validate } from "../../../helpers/gesture";
import styles from "./index.css";
import deferredStyles from "./deferred.css";
import { ENTER_CODE, ESCAPE_CODE } from "./constants";

const code = event => event.code;

const Dropdown = ({
  body,
  children,
  footer,
  formatTranslation,
  header,
  isHidden,
  name,
  onOpen,
  onClose,
  isOpen,
  preventOpenOnMouseEnter,
  preventCloseOnMouseLeave,
  closeButton
}) => {
  const bodyRef = useRef(null);

  const wrapperClassnames = cx(styles.wrapper, {
    [styles.wrapper__open]: isOpen,
    [styles.wrapper__hidden]: isHidden
  });

  const contentClassnames = cx(deferredStyles.content, {
    [deferredStyles.content__displayed]: isOpen
  });

  const onClick = event => handleClick(event);

  const handleClick = event =>
    isOpen ? validate(event, onClose) : validate(event, onOpen);

  const onMouseEnter = event => {
    if (preventOpenOnMouseEnter) {
      return;
    }
    validate(event, onOpen);
  };

  const onMouseLeave = event => {
    if (preventCloseOnMouseLeave) {
      return;
    }
    validate(event, onClose);
  };

  const closeWithMouse = event => {
    event.stopPropagation();
    validate(event, onClose);
  };

  const handleKeyDown = event => {
    if (isOpen && code(event) === ESCAPE_CODE) {
      validate(event, onClose);
    }
  };

  const handleEnterKeyDown = event => {
    if (code(event) === ENTER_CODE) {
      onClose(event);
    }
  };

  useEffect(() => {
    if (isOpen && bodyRef.current) {
      bodyRef.current.scrollTop = 0;
    }
  }, [isOpen]);

  return (
    // <div> element is catching clicks and key events from {children}
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      id={`${name}Dropdown`}
      className={wrapperClassnames}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onKeyDown={handleKeyDown}
    >
      {children}
      <div
        className={styles.outerWrapper}
        data-testid={`${name.toLowerCase()}-dropdown`}
        id={`${name.toLowerCase()}-dropdown`}
      >
        <div className={styles.innerWrapper}>
          <div className={styles.panel}>
            <div className={contentClassnames}>
              <div className={deferredStyles.header}>
                {header && cloneElement(header, { focusable: isOpen })}
                {closeButton && (
                  <IconButton
                    icon={deferredStyles.close}
                    onClick={closeWithMouse}
                    onKeyDown={handleEnterKeyDown}
                    className={styles.close}
                    data-testid={`${name.toLowerCase()}-close-btn`}
                    aria-label={formatTranslation("icon_close")}
                  />
                )}
              </div>
              <div className={deferredStyles.body} ref={bodyRef}>
                {body &&
                  cloneElement(body, {
                    focusable: isOpen,
                    queryParams: { nlid: "nav header" }
                  })}
              </div>
              {footer && (
                <div className={deferredStyles.footer}>
                  {cloneElement(footer, { focusable: isOpen })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

Dropdown.propTypes = {
  body: PropTypes.element,
  children: PropTypes.node,
  footer: PropTypes.element,
  formatTranslation: PropTypes.func.isRequired,
  header: PropTypes.element,
  isHidden: PropTypes.bool,
  isOpen: PropTypes.bool,
  closeButton: PropTypes.bool,
  name: PropTypes.string.isRequired,
  onClose: PropTypes.func,
  onOpen: PropTypes.func,
  preventCloseOnMouseLeave: PropTypes.bool,
  preventOpenOnMouseEnter: PropTypes.bool
};

export default withTranslation(Dropdown);
